import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Stack,
  Alert,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import { CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import GeneralInfo from '../../components/dashboard/GeneralInfo';
import api from '../../services/api';
import { grey, primary, fontSize, fontWeight, radius } from '../../styles/tokens';

/**
 * Dashboard Client - Mobile First
 *
 * Vue Apercu du portail client:
 * - Affiche le resume du plan actif de la semaine
 * - Lecture seule (pas de modification)
 */
const ClientDashboard = () => {
  const { user } = useContext(AuthContext);

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        // Recuperer le plan actif du client
        const planData = await api.get('/client/weekplan/current');

        if (!planData || !planData.dayPlans) {
          setError('no_plan');
          setLoading(false);
          return;
        }

        // Normaliser client -> user pour compatibilite avec les composants
        if (planData.client && !planData.user) {
          planData.user = planData.client;
        }

        setPlan(planData);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la recuperation du plan:', err.message);
        if (err.message?.includes('Aucun plan') || err.response?.status === 404) {
          setError('no_plan');
        } else {
          setError(err.message);
        }
        setLoading(false);
      }
    };

    if (user) {
      fetchCurrentPlan();
    }
  }, [user]);

  // Calcul de la date de la semaine
  const getWeekDateRange = () => {
    if (!plan?.startDate) return null;
    const start = new Date(plan.startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const options = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('fr-FR', options)} - ${end.toLocaleDateString('fr-FR', options)}`;
  };

  // Loading state
  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: radius.lg, mb: 2 }} />
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: radius.md, mb: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: radius.lg }} />
      </Box>
    );
  }

  // No plan state
  if (error === 'no_plan') {
    return (
      <>
        <Helmet>
          <title>Mon Programme - Yoco</title>
        </Helmet>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            px: 2,
          }}
        >
          <CalendarIcon sx={{ fontSize: 64, color: grey[300], mb: 2 }} />
          <Typography
            sx={{
              fontSize: fontSize['2xl'],
              fontWeight: fontWeight.semibold,
              color: grey[800],
              mb: 1,
            }}
          >
            Aucun programme actif
          </Typography>
          <Typography
            sx={{
              fontSize: fontSize.base,
              color: grey[500],
              mb: 3,
              maxWidth: 300,
            }}
          >
            Votre coach n'a pas encore publie de programme pour vous.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: grey[600],
              borderColor: grey[300],
            }}
          >
            Contacter mon coach
          </Button>
        </Box>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const weekNumber = plan?.weekNumber || '?';
  const dateRange = getWeekDateRange();

  return (
    <>
      <Helmet>
        <title>{`Semaine ${weekNumber} - Mon Programme - Yoco`}</title>
      </Helmet>

      <Box>
        {/* Header Card - Resume de la semaine */}
        <Card sx={{ mb: 2, borderRadius: radius.lg }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography
                  sx={{
                    fontSize: fontSize.xs,
                    color: grey[500],
                    fontWeight: fontWeight.medium,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Programme actif
                </Typography>
                <Typography
                  sx={{
                    fontSize: fontSize['2xl'],
                    fontWeight: fontWeight.bold,
                    color: grey[900],
                  }}
                >
                  Semaine {weekNumber}
                </Typography>
                {dateRange && (
                  <Typography
                    sx={{
                      fontSize: fontSize.sm,
                      color: grey[500],
                    }}
                  >
                    {dateRange}
                  </Typography>
                )}
              </Box>
              {plan?.user?.targetMacros?.calories && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    sx={{
                      fontSize: fontSize['2xl'],
                      fontWeight: fontWeight.bold,
                      color: grey[900],
                    }}
                  >
                    {plan.user.targetMacros.calories}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: fontSize.xs,
                      color: grey[500],
                    }}
                  >
                    kcal/jour
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Apercu Content */}
        <GeneralInfo
          user={plan.user}
          dietaryPreferences={plan.user?.dietaryPreferences}
          goals={plan.user?.goals}
          allergies={plan.user?.intolerances}
          summary={plan.summary}
          targetMacros={plan.user?.targetMacros}
          planId={plan._id}
          isClientView={true}
        />
        {plan.recommendations && (
          <Card sx={{ mt: 2, borderRadius: radius.lg }}>
            <CardContent>
              <Typography
                sx={{
                  fontSize: fontSize.sm,
                  fontWeight: fontWeight.semibold,
                  color: grey[700],
                  mb: 1,
                }}
              >
                Conseils de votre coach
              </Typography>
              <Typography
                sx={{
                  fontSize: fontSize.base,
                  color: grey[600],
                  whiteSpace: 'pre-wrap',
                }}
              >
                {plan.recommendations}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </>
  );
};

export default ClientDashboard;
